import { currencyFormat } from "simple-currency-format";
import ApiWrapper from "../general/ApiWrapper";
import { setTicketData } from "../../store/Store";
import i18n from "i18next";

// Api to cancel stripe payment intent
export const cancelPaymentIntent = async (
  accessToken,
  paymentIntent,
  companyId
) => {
  if (!accessToken || !paymentIntent || !companyId) return;

  const response = await ApiWrapper.delete(
    `/api/mkp/v1/user/payment_methods/${paymentIntent?.id}/cancel`,
    {
      params: {
        payment_intent: paymentIntent,
        company_id: companyId,
      },
    }
  );

  return response.data;
};

// API to calculate surcharge.
//
// `amount` is the amount actually being charged (getBalanceDue), which includes
// any tip added on this page. Pass it so the surcharge is computed on the real
// charge: when it is omitted the BE falls back to the access token's pinned
// `amount`, a snapshot of the balance when the link was generated that excludes
// the tip, so the surcharge comes out too low. The BE coerces this query param
// to a decimal before use.
export const calculateSurcharge = async (
  accessToken,
  cardType,
  cardCountry,
  amount
) => {
  if (!accessToken || !cardType) return;

  try {
    const params = {
      card_type: cardType,
    };

    if (cardCountry) params.card_country = cardCountry;
    // Only send a real, positive amount. getBalanceDue returns 0 before the
    // ticket has loaded; sending 0 would have the BE compute a 0 surcharge
    // instead of falling back to the ticket's own balance.
    if (amount > 0) params.amount = amount;

    const response = await ApiWrapper.get(
      "/api/mkp/v1/intake/surcharge/calculate_surcharge",
      { accessToken, params }
    );
    return response.data;
  } catch (error) {
    return {
      surcharge: {
        successful: false,
        error: error,
      },
    };
  }
};

// =================Surcharge Helpers =============
export const isSurchargeApplicable = (ticketData) => {
  return ticketData?.resource?.company?.data?.enable_credit_card_surcharging;
};

export const isCloud9 = (ticketData) =>
  ticketData?.resource?.payment_account?.type === "StripeAccount::Cloud9";

// Whether to send surcharge_amount as a separate field to the transactions API.
// Off when disable_cloud9_surcharge_passthrough is set on the company.
export const isCloud9SurchargePassthroughEnabled = (ticketData) => {
  if (!isCloud9(ticketData)) return false;
  return !ticketData?.resource?.company?.data
    ?.disable_cloud9_surcharge_passthrough;
};

export const isSurchargeRatesV2Enabled = (ticketData) => {
  return ticketData?.resource?.company?.data?.enable_surcharge_rates_v2;
};

export const resolveCardFundingType = (card) => {
  if (!card) return null;
  if (card.display_brand?.startsWith("eftpos_au")) return "eftpos";
  return card.funding;
};

const cloneAccessToken = (accessToken) => {
  if (!accessToken?.data?.payload) return { ...accessToken };
  return {
    ...accessToken,
    data: {
      ...accessToken.data,
      payload: { ...accessToken.data.payload },
    },
  };
};

const getAdjustments = (ticketData) => ticketData?.resource?.adjustments || [];

const updateTicketData = (ticketData, resourceUpdates = {}) => {
  if (!ticketData) return;

  const updatedAccessToken = cloneAccessToken(ticketData.access_token);

  const updatedTicketData = {
    ...ticketData,
    resource: {
      ...ticketData.resource,
      ...resourceUpdates,
    },
    access_token: updatedAccessToken,
  };

  setTicketData(updatedTicketData);
};

export const addSurchargeToTicketAdjustments = (ticketData, surcharge) => {
  if (!ticketData || !surcharge?.successful) return;

  const newAdjustment = {
    adjustable_type: "Pos::SurchargeItem",
    label: getLabelTextForSurchargeItem({
      ...surcharge.surcharge_configuration_item,
      currency: ticketData?.resource?.company?.currency_uuid,
      locale: i18n.language,
    }),
    amount: surcharge.surcharge_amount,
  };

  // Filter out any existing local surcharge to keep this idempotent
  const adjustmentsWithoutLocalSurcharge = getAdjustments(ticketData).filter(
    (adj) => adj?.adjustable_type !== "Pos::SurchargeItem" || adj?.id
  );

  updateTicketData(ticketData, {
    adjustments: [...adjustmentsWithoutLocalSurcharge, newAdjustment],
    calculateSurcharge: true,
    surcharge_amount: surcharge.surcharge_amount,
  });
};

export const removeSurchargeFromTicketAdjustments = (ticketData) => {
  if (!ticketData) return;

  const adjustments = getAdjustments(ticketData);
  const hasSurchargeItems = adjustments.some(
    (adj) => adj?.adjustable_type === "Pos::SurchargeItem" && !adj?.id
  );
  if (!hasSurchargeItems) return;

  updateTicketData(ticketData, {
    adjustments: adjustments.filter(
      (adj) => adj?.adjustable_type !== "Pos::SurchargeItem" || adj?.id
    ),
    surcharge_amount: null,
    calculateSurcharge: false,
  });
};

export const getLabelTextForSurchargeItem = (item) => {
  if (!item || typeof item !== "object") return "";

  const {
    flat_amount = 0,
    percentage_amount = 0,
    currency = "USD",
    locale = "en-US",
  } = item;

  if (!flat_amount && !percentage_amount) return "Surcharge";

  const parts = [];

  if (percentage_amount > 0) {
    const raw = String(Number(percentage_amount));
    parts.push(`${raw.includes(".") ? raw : `${raw}.0`}%`);
  }

  if (flat_amount > 0) {
    parts.push(
      currencyFormat(Number(flat_amount), locale, currency.toUpperCase(), 2)
    );
  }

  return `Surcharge (${parts.join(" + ")})`;
};
